import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import BrandLogo from "./BrandLogo";
import LanguageSwitcher from "./LanguageSwitcher";

/** Top padding under notch / Dynamic Island (Tailwind arbitrary value). */
const NAV_TOP_SAFE = "pt-[max(0.75rem,env(safe-area-inset-top))]";

export default function Nav() {
  const { content } = useLanguage();
  const { nav, activeRoutes } = content;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const routesLabel =
    activeRoutes?.sectionLabel?.replace(/^\d+\s*·\s*/, "").trim() || "Routes";

  const navLinks = [
    { href: "#why", label: nav.whyUs, testId: "nav-why" },
    { href: "#how", label: nav.howItWorks, testId: "nav-how" },
    { href: "#routes", label: routesLabel, testId: "nav-routes" },
    { href: "#services", label: nav.offerings, testId: "nav-services" },
    { href: "#faq", label: nav.faq, testId: "nav-faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);


  return (
    <>
      <motion.nav
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 ${NAV_TOP_SAFE} px-4 pb-3 sm:px-6 md:px-12 lg:px-20 md:pb-4 flex items-center justify-between gap-3 transition-all duration-500 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0] shadow-sm"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="main-nav"
      >
        <BrandLogo variant="onDark" className="h-8 shrink-0 sm:h-10 md:h-11" />

        <ul
          className={`hidden md:flex items-center gap-6 lg:gap-8 text-sm font-medium ${
            scrolled ? "text-[#0D1B2A]/75" : "text-white/80"
          }`}
        >
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="hover:text-[#2563EB] transition-colors py-2"
                data-testid={link.testId}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher scrolled={scrolled} />
          <a
            href="#contact"
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-[#2563EB] px-3 py-2.5 text-xs font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] transition-all duration-300 hover:bg-[#1d4fd8] sm:px-5 sm:text-sm"
            data-testid="nav-cta-join"
          >
            {nav.cta}
          </a>
          <button
            type="button"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border md:hidden touch-manipulation ${
              scrolled
                ? "border-[#E2E8F0] text-[#0D1B2A] hover:bg-[#F1F5F9]"
                : "border-white/30 text-white hover:bg-white/10"
            }`}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-[#0D1B2A]/60 backdrop-blur-sm"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full w-[min(100%,20rem)] flex-col bg-white shadow-2xl pt-[max(4.5rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
            <nav className="flex flex-1 flex-col gap-1 px-4 py-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="rounded-xl px-4 py-3.5 text-base font-medium touch-manipulation text-[#0D1B2A] bg-[#F8FAFC] hover:bg-[#EEF2FF]"
                  data-testid={`${link.testId}-mobile`}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                className="mt-4 flex min-h-[48px] items-center justify-center rounded-xl bg-[#2563EB] px-4 py-3.5 text-center text-base font-semibold text-white touch-manipulation"
                onClick={() => setMenuOpen(false)}
              >
                {nav.cta}
              </a>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
